import { SetItem, SogliaItem, ProgressionRule, MiniSet } from "../types/Entities";

/**
 * Motore di calcolo per progressioni precompilate e programmazioni.
 *
 * Tipi di progressione:
 *   set-to-set  → carico cresce/decresce tra settimane (stesso set, settimana successiva)
 *   set-in-set  → carico cresce/decresce tra set nella stessa settimana (set successivo, stessa settimana)
 *   both        → entrambi
 *   none        → nessun calcolo automatico
 *
 * Priorità calcolo (calculationPriority):
 *   "fixed"  → applica sempre la % o l'n fissa, indipendentemente dalle reps effettive
 *   "soglie" → (default) calcola solo se le work reps escono dal range; usa soglie array per il delta
 */

function parseNum(v: string | number | undefined): number {
  if (v === undefined || v === "" || v === null) return 0;
  const n = typeof v === "string" ? parseFloat(v) : v;
  return isNaN(n) ? 0 : n;
}

/**
 * Estrae il numero minimo di reps dallo schema del trainer.
 * Esempi: "8" → 8 | "5-9" → 5 | "3x8" → 8 | "3x5-9" → 5
 */
export function parseMinReps(repScheme?: string): number | undefined {
  if (!repScheme) return undefined;
  const s = repScheme.trim().replace(/^\d+x/i, "");
  const rangeMatch = s.match(/^(\d+)\s*[-–]\s*\d+/);
  if (rangeMatch) return parseInt(rangeMatch[1], 10);
  const numMatch = s.match(/^(\d+)/);
  if (numMatch) return parseInt(numMatch[1], 10);
  return undefined;
}

/**
 * Estrae il numero massimo di reps dallo schema del trainer.
 * Esempi: "8" → 8 | "5-9" → 9 | "3x8" → 8 | "3x5-9" → 9
 */
export function parseMaxReps(repScheme?: string): number | undefined {
  if (!repScheme) return undefined;
  const s = repScheme.trim().replace(/^\d+x/i, "");
  const rangeMatch = s.match(/^\d+\s*[-–]\s*(\d+)/);
  if (rangeMatch) return parseInt(rangeMatch[1], 10);
  const numMatch = s.match(/^(\d+)/);
  if (numMatch) return parseInt(numMatch[1], 10);
  return undefined;
}

/**
 * Trova la soglia applicabile: la più alta con threshold ≤ delta.
 */
export function findMatchingSoglia(soglie: SogliaItem[], delta: number): SogliaItem | undefined {
  return soglie
    .filter((s) => s.threshold <= delta)
    .sort((a, b) => b.threshold - a.threshold)[0];
}

/**
 * Estrae il valore numerico effettivo da una SogliaItem.
 * Supporta sia il nuovo campo `value` che il legacy `percentage`.
 */
export function getSogliaNumericValue(s: SogliaItem): number {
  const v = s.value !== undefined ? s.value : s.percentage;
  return typeof v === "string" ? parseFloat(v) || 0 : (v ?? 0);
}

/**
 * Returns a SogliaItem's value as a rep-scheme string when it is shaped like
 * "10", "8-10", "8–10" (en-dash) or "3x8-10". Otherwise undefined. Used by
 * the reps-target engine paths to preserve range schemes instead of collapsing
 * "8-10" to 8 via parseFloat.
 */
export function getSogliaSchemeValue(s: SogliaItem): string | undefined {
  const raw = s.value !== undefined ? s.value : s.percentage;
  if (typeof raw !== "string") return undefined;
  const v = raw.trim();
  if (/^\d+(\s*[-–]\s*\d+)?$/.test(v)) return v;
  if (/^\d+x\d+(-\d+)?$/i.test(v)) return v;
  return undefined;
}

/**
 * True when a value is a rep-scheme range string (min < max). A bare "8" is
 * NOT a range. Used to override declared rule.type when rule.value happens to
 * be range-shaped (decisione confermata: range-shape wins over declared type).
 */
export function isRepsRange(v: unknown): boolean {
  if (typeof v !== "string") return false;
  const min = parseMinReps(v);
  const max = parseMaxReps(v);
  return min !== undefined && max !== undefined && min !== max;
}

/**
 * Applica una ProgressionRule a un baseLoad.
 * Supporta i tipi: %, weight, %nRM, WnRM, N.
 * repScheme è gestito a livello superiore (ritorna il repScheme come stringa).
 */
export function applyRule(
  baseLoad: number,
  rule: ProgressionRule,
  isIncrement: boolean,
  overrideSoglie?: SogliaItem[],
  delta?: number
): number {
  const soglie = overrideSoglie ?? rule.soglie;
  let effectiveValue: number;

  if (soglie && soglie.length > 0 && delta !== undefined) {
    const matched = findMatchingSoglia(soglie, delta);
    if (!matched) return baseLoad;
    effectiveValue = getSogliaNumericValue(matched);
  } else {
    effectiveValue = typeof rule.value === "string" ? parseFloat(rule.value) || 0 : rule.value;
  }

  if (effectiveValue <= 0) return baseLoad;

  switch (rule.type) {
    case "%":
    case "%nRM":
      return isIncrement
        ? Math.round(baseLoad * (1 + effectiveValue / 100) * 100) / 100
        : Math.round(baseLoad * (1 - effectiveValue / 100) * 100) / 100;
    case "weight":
    case "N":
    case "WnRM":
      return isIncrement
        ? Math.round((baseLoad + effectiveValue) * 100) / 100
        : Math.round(Math.max(0, baseLoad - effectiveValue) * 100) / 100;
    default:
      return baseLoad;
  }
}

/**
 * Calcola il carico ipotetico (1RM stimato via formula di Epley).
 * workLoad × (1 + workReps / 30), arrotondato a 2 decimali.
 */
export function computeEpley(workReps: number, workLoad: number): number | undefined {
  if (workLoad <= 0 || workReps <= 0) return undefined;
  return Math.round(workLoad * (1 + workReps / 30) * 100) / 100;
}

/**
 * Wave 1.2 (#8789b0ba) — range-aware delta resolution for soglie.
 *
 * Returns the delta + direction to feed into `findMatchingSoglia`, or undefined
 * when no soglia path applies. Three branches:
 *   1. workReps > maxReps → over-range, positive (delta = workReps - maxReps).
 *   2. workReps < minReps → under-range, negative (delta = minReps - workReps).
 *   3. Range repScheme (min < max) AND workReps ∈ (minReps, maxReps] →
 *      in-range positive trigger with delta = 0. ONLY fires when the caller
 *      supplies a positive soglie array containing a `threshold === 0` entry.
 *      `workReps === minReps` does NOT trigger (boundary excluded per spec).
 *      This guard keeps flat-value rules (no soglie) from firing in-range,
 *      preserving the F2 "intent-only midpoint suppresses nextWeek" semantics.
 *
 * Single-value repSchemes (min === max) preserve existing behavior: only
 * strict over/under triggers, no in-range firing.
 */
function resolveSogliaDelta(
  set: SetItem,
  workReps: number,
  positiveSoglie?: SogliaItem[] | null,
): { delta: number; isDecrement: boolean } | undefined {
  const minReps = parseMinReps(set.repScheme);
  const maxReps = parseMaxReps(set.repScheme);
  if (maxReps !== undefined && workReps > maxReps) {
    return { delta: workReps - maxReps, isDecrement: false };
  }
  if (minReps !== undefined && workReps < minReps) {
    return { delta: minReps - workReps, isDecrement: true };
  }
  if (
    minReps !== undefined &&
    maxReps !== undefined &&
    minReps < maxReps &&
    workReps > minReps &&
    workReps <= maxReps &&
    positiveSoglie?.some((s) => s.threshold === 0)
  ) {
    return { delta: 0, isDecrement: false };
  }
  return undefined;
}

// ─── Calcolo carico suggerito (StS) ──────────────────────────────────────────

/**
 * Calcola il carico suggerito per la settimana successiva (Set-to-Set).
 * Usa workLoad del set corrente come base.
 *
 * Priorità "fixed": applica sempre la % fissa (ignora repScheme range).
 * Priorità "soglie" (default): attiva solo se workReps è fuori dal range.
 *
 * Prima usa incrementRuleStS/decrementRuleStS se presenti, altrimenti i campi legacy.
 */
export function computeNextWeekLoad(set: SetItem, workReps: number): number | undefined {
  if (set.progressionType === "none") return undefined;

  const baseLoad = parseNum(set.workLoad);
  if (baseLoad <= 0) return undefined;

  const priority = set.calculationPriority ?? "soglie";

  if (priority === "fixed") {
    const pct = set.fixedIncrement ?? (set.percentageSetToSet ?? 0);
    if (pct <= 0) return undefined;
    const isInc = set.incrementSetToSet !== false;
    return Math.round(baseLoad * (1 + (isInc ? 1 : -1) * pct / 100) * 100) / 100;
  }

  // "soglie" priority: delta-based (range-aware via resolveSogliaDelta).
  // Pass the positive (increment) soglie source so the in-range delta=0
  // branch only fires when a threshold-0 soglia actually exists.
  const positiveSoglieStS = set.incrementRuleStS?.soglie ?? set.soglieSetToSet;
  const resolved = resolveSogliaDelta(set, workReps, positiveSoglieStS);
  if (!resolved) return undefined;
  const { delta, isDecrement } = resolved;

  // Use new ProgressionRule if available
  const rule = isDecrement ? set.decrementRuleStS : set.incrementRuleStS;
  if (rule) {
    if (rule.type === "repScheme") return undefined; // repScheme handled separately
    return applyRule(baseLoad, rule, !isDecrement, undefined, delta);
  }

  // Legacy fallback
  const soglie = set.soglieSetToSet;
  let percentage: number | undefined;

  if (soglie && soglie.length > 0) {
    const matched = findMatchingSoglia(soglie, delta);
    if (!matched) return undefined;
    percentage = getSogliaNumericValue(matched);
  } else {
    const incValue = parseNum(set.percentageSetToSet);
    if (incValue <= 0) return undefined;
    percentage = incValue;
  }

  if (!percentage || percentage <= 0) return undefined;

  return Math.round(baseLoad * (isDecrement ? 1 - percentage / 100 : 1 + percentage / 100) * 100) / 100;
}

// ─── Calcolo carico suggerito (SiS) ──────────────────────────────────────────

/**
 * Calcola il carico suggerito per il set successivo nella stessa settimana (Set-in-Set).
 * Prima usa incrementRuleSiS/decrementRuleSiS se presenti, altrimenti i campi legacy.
 */
export function computeNextSetInSetLoad(set: SetItem, workReps: number): number | undefined {
  if (set.progressionType === "none") return undefined;

  const baseLoad = parseNum(set.workLoad);
  if (baseLoad <= 0) return undefined;

  const priority = set.calculationPriority ?? "soglie";

  if (priority === "fixed") {
    const pct = set.fixedIncrement ?? (set.percentageSetInSet ?? 0);
    if (pct <= 0) return undefined;
    const isInc = set.incrementSetInSet !== false;
    return Math.round(baseLoad * (1 + (isInc ? 1 : -1) * pct / 100) * 100) / 100;
  }

  // "soglie" priority: delta-based (range-aware via resolveSogliaDelta).
  const positiveSoglieSiS = set.incrementRuleSiS?.soglie ?? set.soglieSetInSet;
  const resolved = resolveSogliaDelta(set, workReps, positiveSoglieSiS);
  if (!resolved) return undefined;
  const { delta, isDecrement } = resolved;

  // Use new ProgressionRule if available
  const rule = isDecrement ? set.decrementRuleSiS : set.incrementRuleSiS;
  if (rule) {
    if (rule.type === "repScheme") return undefined;
    return applyRule(baseLoad, rule, !isDecrement, undefined, delta);
  }

  // Legacy fallback
  const soglie = set.soglieSetInSet;
  let percentage: number | undefined;

  if (soglie && soglie.length > 0) {
    const matched = findMatchingSoglia(soglie, delta);
    if (!matched) return undefined;
    percentage = getSogliaNumericValue(matched);
  } else {
    const incValue = parseNum(set.percentageSetInSet);
    if (incValue <= 0) return undefined;
    percentage = incValue;
  }

  if (!percentage || percentage <= 0) return undefined;

  return Math.round(baseLoad * (isDecrement ? 1 - percentage / 100 : 1 + percentage / 100) * 100) / 100;
}

// ─── Calcolo carico suggerito (X-Reps) ───────────────────────────────────────

/**
 * Calcola il carico suggerito per le X-Reps della settimana successiva.
 * Base = workXLoad; usa soglieXReps per il delta rispetto al target xReps.
 */
export function computeNextWeekXRepsLoad(set: SetItem, workXReps: number): number | undefined {
  const baseLoad = parseNum(set.workXLoad);
  if (baseLoad <= 0) return undefined;

  const targetXReps = parseNum(set.xReps);
  if (targetXReps <= 0) return undefined;

  let delta: number;
  let isDecrement: boolean;

  if (workXReps > targetXReps) {
    delta = workXReps - targetXReps;
    isDecrement = false;
  } else if (workXReps < targetXReps) {
    delta = targetXReps - workXReps;
    isDecrement = true;
  } else {
    return undefined;
  }

  const soglie = set.soglieXReps;
  let percentage: number | undefined;

  if (soglie && soglie.length > 0) {
    const matched = findMatchingSoglia(soglie, delta);
    if (!matched) return undefined;
    percentage = getSogliaNumericValue(matched);
  } else {
    return undefined;
  }

  if (!percentage || percentage <= 0) return undefined;

  const newLoad = isDecrement
    ? baseLoad * (1 - percentage / 100)
    : baseLoad * (1 + percentage / 100);

  return Math.round(newLoad * 100) / 100;
}

// ─── Ricalcolo SiS dopo riordino ─────────────────────────────────────────────

/**
 * Ricalcola i carichi suggeriti SiS dopo un riordino di set nella stessa settimana.
 * Per ogni set con workReps impostato, aggiorna il carico suggerito del set successivo.
 */
export function recalculateSiSLoads(sets: SetItem[]): SetItem[] {
  const updated = [...sets];
  for (let i = 0; i < updated.length - 1; i++) {
    const set = updated[i];
    const progType = set.progressionType;
    if (progType !== "set-in-set" && progType !== "both") continue;
    const workRepsVal = parseFloat(String(set.workReps ?? ""));
    if (isNaN(workRepsVal) || workRepsVal <= 0) continue;
    const nextLoad = computeNextSetInSetLoad(set, workRepsVal);
    if (nextLoad !== undefined) {
      updated[i + 1] = { ...updated[i + 1], load: String(nextLoad) };
    }
  }
  return updated;
}

// ─── F4: Reps-target progression ─────────────────────────────────────────────

/**
 * Computes the next-week target reps when a progression rule's target is "reps".
 * Returns the new repScheme as a string when the rule is `repScheme`, otherwise
 * returns the new numeric reps target derived from the current workReps.
 *
 * Mirrors `computeNextWeekLoad` but operates on reps. Honors `progressionType: "none"`,
 * `calculationPriority: "fixed"`, and the soglie/delta path for `incrementRuleStS`.
 */
export function computeNextWeekReps(set: SetItem, workReps: number):
  | { kind: "scheme"; repScheme: string }
  | { kind: "reps"; reps: number }
  | undefined {
  if (set.progressionType === "none") return undefined;
  if (workReps <= 0) return undefined;

  const resolved = resolveSogliaDelta(set, workReps, set.incrementRuleStS?.soglie);
  if (!resolved) return undefined;

  const rule = resolved.isDecrement ? set.decrementRuleStS : set.incrementRuleStS;
  if (!rule) return undefined;

  // Wave 2.4 (#3b91080e) — BREAKING. Reps targets are now ABSOLUTE, not deltas.
  // Soglia path: matched `value` IS the new reps target.
  // Flat path (no soglia): rule.value IS the new reps target.
  // Direction (isDecrement) only gates which rule (increment/decrement) was
  // selected upstream; it no longer flips the sign of any delta. A range-shape
  // value (e.g. "8-10") wins over the declared rule.type and emits as scheme.
  const soglie = rule.soglie;
  if (soglie && soglie.length > 0) {
    const matched = findMatchingSoglia(soglie, resolved.delta);
    if (!matched) return undefined;
    const scheme = getSogliaSchemeValue(matched);
    if (scheme !== undefined) return { kind: "scheme", repScheme: scheme };
    const absoluteReps = Math.round(getSogliaNumericValue(matched));
    if (absoluteReps <= 0) return undefined;
    return { kind: "reps", reps: absoluteReps };
  }
  // repScheme rule (no soglie): rule.value IS the new scheme string.
  if (rule.type === "repScheme") {
    const v = typeof rule.value === "string" ? rule.value : String(rule.value);
    if (!v) return undefined;
    return { kind: "scheme", repScheme: v };
  }
  if (isRepsRange(rule.value)) {
    return { kind: "scheme", repScheme: (rule.value as string).trim() };
  }
  const flat = typeof rule.value === "string" ? parseFloat(rule.value) : rule.value;
  if (!Number.isFinite(flat)) return undefined;
  const absoluteReps = Math.round(flat as number);
  if (absoluteReps <= 0) return undefined;
  return { kind: "reps", reps: absoluteReps };
}

// ─── F2 + F5: Unified simulate ───────────────────────────────────────────────

/**
 * A single hypothetical value the FE renders. Each projection (currentWeek,
 * nextWeek, nextSetInSet, base) carries its own `kind` so a set with a
 * load-target StS rule and a reps-target SiS rule renders correctly:
 *
 *   nextWeek      → { kind: "load", value: 102.5 }     // "102.5 kg"
 *   nextSetInSet  → { kind: "reps", value: 14, repScheme: "10-14" }  // "10-14 reps"
 *
 * When `kind === "reps"` and the rule was `repScheme`, `repScheme` carries the
 * new scheme string so the FE can render "10-14 reps" instead of just a single
 * number.
 */
export type Projection =
  | { kind: "load"; value: number }
  | { kind: "reps"; value: number; repScheme?: string };

export interface SimulationOutput {
  /**
   * Current entered base values, unchanged. Useful for:
   * - F9 "None" preview (the projection equals base for all slots).
   * - Dual-sim layout where the FE shows "base → projected" deltas.
   * Always populated when the relevant inputs exist.
   */
  base?: Projection;
  /**
   * Current-week Epley 1RM (kind: "load") — only populated when useWork=true
   * AND workReps + workLoad are present. When useWork=false, undefined
   * (there's no projection-only current-week hypothetical to compute).
   */
  currentWeek?: Projection;
  /** Set-to-set suggestion. `kind` follows incrementRuleStS / decrementRuleStS. */
  nextWeek?: Projection;
  /** Set-in-set suggestion. `kind` follows incrementRuleSiS / decrementRuleSiS. */
  nextSetInSet?: Projection;
}

export interface SimulationOptions {
  /**
   * When true, compute outputs based on the set's logged workReps/workLoad
   * (engine + actual user-logged work). When false, compute outputs based on
   * the progression parameters alone (effective intent, ignoring whatever the
   * user happened to log).
   *
   * F2 dual-sim renders both: a `useWork: false` panel ("(a) progression
   * alone") and a `useWork: true` panel ("(b) progression + work").
   */
  useWork: boolean;
  /**
   * When present, the engine uses these values in place of `set.workReps` /
   * `set.workLoad` as the driving baseline. Additive: absent fields fall back
   * to the set's own work fields. Behavior is unchanged when omitted.
   */
  workOverride?: { reps?: number | string; load?: number };
}

/**
 * SiS-side reps target — mirrors `computeNextWeekReps` but reads
 * incrementRuleSiS / decrementRuleSiS. Returns the new repScheme string when
 * the rule type is `repScheme`, otherwise an integer reps target derived from
 * the soglia step (or +/- 1).
 */
export function computeNextSetInSetReps(set: SetItem, workReps: number):
  | { kind: "scheme"; repScheme: string }
  | { kind: "reps"; reps: number }
  | undefined {
  if (set.progressionType === "none") return undefined;
  if (workReps <= 0) return undefined;

  const resolved = resolveSogliaDelta(set, workReps, set.incrementRuleSiS?.soglie);
  if (!resolved) return undefined;

  const rule = resolved.isDecrement ? set.decrementRuleSiS : set.incrementRuleSiS;
  if (!rule) return undefined;

  // Wave 2.4 (#3b91080e) — BREAKING. Reps targets are now ABSOLUTE, mirroring
  // computeNextWeekReps. Soglia value / flat value IS the new reps target. A
  // range-shape value wins over the declared rule.type and emits as scheme.
  if (rule.soglie && rule.soglie.length > 0) {
    const matched = findMatchingSoglia(rule.soglie, resolved.delta);
    if (!matched) return undefined;
    const scheme = getSogliaSchemeValue(matched);
    if (scheme !== undefined) return { kind: "scheme", repScheme: scheme };
    const absoluteReps = Math.round(getSogliaNumericValue(matched));
    if (absoluteReps <= 0) return undefined;
    return { kind: "reps", reps: absoluteReps };
  }
  if (rule.type === "repScheme") {
    const v = typeof rule.value === "string" ? rule.value : String(rule.value);
    if (!v) return undefined;
    return { kind: "scheme", repScheme: v };
  }
  if (isRepsRange(rule.value)) {
    return { kind: "scheme", repScheme: (rule.value as string).trim() };
  }
  const flat = typeof rule.value === "string" ? parseFloat(rule.value) : rule.value;
  if (!Number.isFinite(flat)) return undefined;
  const absoluteReps = Math.round(flat as number);
  if (absoluteReps <= 0) return undefined;
  return { kind: "reps", reps: absoluteReps };
}

/** Returns true if the named rule on `set` targets reps (vs load). */
const ruleTargetsReps = (rule: { type?: string } | undefined): boolean => {
  return rule?.type === "repScheme";
};

/** Resolves the reps figure that drives the engine for a given useWork mode. */
const resolveDrivingReps = (set: SetItem, useWork: boolean): number => {
  if (useWork) return parseNum(set.workReps);
  const min = parseMinReps(set.repScheme);
  const max = parseMaxReps(set.repScheme);
  if (min !== undefined && max !== undefined) return Math.round((min + max) / 2);
  if (min !== undefined) return min;
  if (max !== undefined) return max;
  return 0;
};

/**
 * Unified simulation entry point. Returns up to four projections, each with
 * its own discriminated `kind`. See `SimulationOutput` and `Projection` for
 * the shape.
 *
 * Per-feature semantics:
 *   F4 — projection.kind = "reps" when the underlying rule type is `repScheme`,
 *        otherwise "load". StS and SiS projections are independent — a set
 *        with mixed-target rules renders correctly (load nextWeek + reps
 *        nextSetInSet, etc.).
 *   F5/F8 — works for fixed/set-to-set/set-in-set/threshold modes. Threshold
 *           rules pass through `applyRule`'s soglia path; the engine does
 *           NOT short-circuit threshold mode.
 *   F9 — `progressionType: "none"` → nextWeek and nextSetInSet are undefined;
 *        base still populates (it's the unchanged current values).
 *   F10 — nextWeek and nextSetInSet are computed independently from any rule
 *         in scope, regardless of `progressionType`.
 */
export function simulateProgression(set: SetItem, opts: SimulationOptions): SimulationOutput {
  // When workOverride is provided, shadow the set's work fields so every
  // downstream helper sees the override values without mutation.
  const effectiveSet: SetItem =
    opts.workOverride !== undefined
      ? {
          ...set,
          ...(opts.workOverride.reps !== undefined
            ? { workReps: opts.workOverride.reps }
            : {}),
          ...(opts.workOverride.load !== undefined
            ? { workLoad: opts.workOverride.load }
            : {}),
        }
      : set;

  const drivingReps = resolveDrivingReps(effectiveSet, opts.useWork);
  const out: SimulationOutput = {};

  // base — current entered values, unchanged.
  const baseLoad = opts.useWork ? parseNum(effectiveSet.workLoad) : parseNum(effectiveSet.load);
  const baseReps = drivingReps;
  // The base projection's kind reflects the configured StS rule's target —
  // when the configured rule is reps, the base is in reps; otherwise load.
  const stSRule = effectiveSet.incrementRuleStS ?? effectiveSet.decrementRuleStS;
  if (ruleTargetsReps(stSRule)) {
    if (baseReps > 0) {
      out.base = { kind: "reps", value: baseReps, repScheme: effectiveSet.repScheme };
    }
  } else if (baseLoad > 0) {
    out.base = { kind: "load", value: baseLoad };
  } else if (baseReps > 0) {
    // Fallback: no load entered but we have reps — show reps as base.
    out.base = { kind: "reps", value: baseReps, repScheme: effectiveSet.repScheme };
  }

  // currentWeek — Epley 1RM, only when useWork=true with both workReps + workLoad.
  if (opts.useWork) {
    const epley = computeEpley(parseNum(effectiveSet.workReps), parseNum(effectiveSet.workLoad));
    if (epley !== undefined) out.currentWeek = { kind: "load", value: epley };
  }

  // nextWeek — StS projection. Kind follows the StS rule's target.
  if (drivingReps > 0 && effectiveSet.progressionType !== "none") {
    if (ruleTargetsReps(stSRule)) {
      const r = computeNextWeekReps(effectiveSet, drivingReps);
      if (r?.kind === "scheme") {
        out.nextWeek = { kind: "reps", value: drivingReps, repScheme: r.repScheme };
      } else if (r?.kind === "reps") {
        out.nextWeek = { kind: "reps", value: r.reps };
      }
    } else {
      const nw = computeNextWeekLoad(effectiveSet, drivingReps);
      if (nw !== undefined) out.nextWeek = { kind: "load", value: nw };
    }
  }

  // nextSetInSet — SiS projection. Kind follows the SiS rule's target,
  // independent of nextWeek's kind.
  if (drivingReps > 0 && effectiveSet.progressionType !== "none") {
    const siSRule = effectiveSet.incrementRuleSiS ?? effectiveSet.decrementRuleSiS;
    if (ruleTargetsReps(siSRule)) {
      const r = computeNextSetInSetReps(effectiveSet, drivingReps);
      if (r?.kind === "scheme") {
        out.nextSetInSet = { kind: "reps", value: drivingReps, repScheme: r.repScheme };
      } else if (r?.kind === "reps") {
        out.nextSetInSet = { kind: "reps", value: r.reps };
      }
    } else {
      const ns = computeNextSetInSetLoad(effectiveSet, drivingReps);
      if (ns !== undefined) out.nextSetInSet = { kind: "load", value: ns };
    }
  }

  return out;
}


// ─── Wave 3.2 (#f309a975): mini-sets / drop-sets ─────────────────────────────

/**
 * Project the load for each mini-set, applying each row's delta cumulatively
 * to the previous row's load (starting from `baseLoad`).
 *
 *   "%"  → load * (1 - deltaValue / 100)
 *   "kg" → load - deltaValue (clamped at 0)
 *
 * Returns one entry per mini-set with the projected load (rounded to 1 decimal),
 * the row's reps verbatim, and the appliedDelta as it was applied (for UI hints).
 * If `baseLoad` is non-positive or `miniSets` is empty, returns [].
 */
export interface MiniSetProjection {
  reps: number | string;
  load: number;
  appliedDelta: { type: "%" | "kg"; value: number };
}

export function computeMiniSetLoads(
  baseLoad: number,
  miniSets: MiniSet[] | null | undefined,
): MiniSetProjection[] {
  if (!(baseLoad > 0)) return [];
  if (!miniSets || miniSets.length === 0) return [];
  const out: MiniSetProjection[] = [];
  // FE-R (#d3cbe5a5) — every mini-set drops from the parent baseLoad, NOT
  // from the previous row. Previously this used a `runningLoad` accumulator
  // which cascaded e.g. three -10% rows to 90/81/72.9; the user spec
  // requires 90/90/90.
  for (const ms of miniSets) {
    const dv = Number.isFinite(ms.deltaValue) ? ms.deltaValue : 0;
    let load: number;
    if (ms.deltaType === "%") {
      load = baseLoad * (1 - dv / 100);
    } else {
      load = Math.max(0, baseLoad - dv);
    }
    load = Math.round(load * 100) / 100;
    out.push({ reps: ms.reps, load, appliedDelta: { type: ms.deltaType, value: dv } });
  }
  return out;
}
